<template>

    <div ref="menuRef"
        role="navigation"
        class="menu flex items-center px-4 lg:px-0 justify-between"
        :class="[ modeClass, { '--show-menu': show, '--hidden': isMenuHidden }, menuClasses ]"
        :style="menuStyle">

        <div class="relative z-10 flex menu__dc">

            <div class="flex items-center justify-start">

                <RouterLink :to="{ name: Route.Home }" aria-label="Home" class="menu__new-logo">

                    <DCLogo class="block"/>

                </RouterLink>

                <RouterLink :to="{ name: Route.Home }" aria-label="Home" class="lg:hidden">

                    <DC class="menu__dc__logo block"/>

                </RouterLink>

            </div>

        </div>

        <div class="menu__items h-full items-center hidden lg:flex lg:justify-end" :class="{ '--show': show }">

            <ul class="md:w-full h-full sm:space-y-3 flex flex-col lg:flex-row sm:justify-center md:justify-center items-center lg:justify-end">

                <li
                    v-for="(item, key) of data.items" :key="key"
                    class="menu__items__link"
                    :class="{
                        '--contact': item.route.name == Route.Contact,
                        '--zh': item.route.name == Route.Contact && isCNLocale,
                        'lg:hidden': item.route.name === Route.Home
                    }">

                    <AOS type="fade-up" :delay="key * 50" is-group anchor-placement="top-bottom">

                        <RouterLink :to="{ name: item.route.name }">
                            {{ item.title }}
                        </RouterLink>

                    </AOS>

                </li>

                <li class="menu__locale" @click="switchLocale">

                    <div class="menu__locale__inner h-11 w-11 flex items-center justify-center">

                        <Chinese v-if="!isCNLocale"/>

                        <div v-else class="font-body text-sm bold">
                            ENG
                        </div>

                    </div>

                </li>

            </ul>

            <div class="lg:hidden flex justify-center items-center w-full space-x-5 pb-10">

                <SocialMediaButton v-for="(social, index) of socialMedia" :key="index" v-bind="social"/>

            </div>

        </div>

        <div class="flex items-center space-x-3 lg:hidden justify-end z-10 relative cursor-pointer select-none">

            <div class="menu__locale --small" @click="switchLocale">

                <Chinese v-if="!isCNLocale"/>

                <div v-else class="text-sm bold">
                    ENG
                </div>

            </div>

            <div class="menu__hamburger" @click="toggle(!show)">

                <Hamburger :is-open="show"/>

            </div>

        </div>

    </div>

</template>

<script lang="ts">

    import { useStickyMenu } from '../compositions/UseStickyMenu'
    import { defineComponent } from 'vue'
    import { interactWithMenu } from '../compositions/menu/InteractWithMenu'
    import { useMobileMenu } from '../compositions/menu/UseMobileMenu'
    import { useLoopReferences } from '../compositions/UseLoopReferences'
    import { useTranslation } from '../compositions/UseTranslation'
    import { Locale } from '../enums/Locale'
    import { Route } from '../enums/Route'
    import Chinese from './Icons/Chinese.vue'
    import Hamburger from './Icons/Hamburger.vue'
    import DC from './Logos/DC.vue'
    import DCLogo from './Logos/DCLogo.vue'
    import SocialMediaButton from './SocialMediaButton.vue'
    import { useCms } from '../compositions/UseCms'
    // import Paragraph from './Paragraph.vue'

    export default defineComponent({
        name: 'Menu',
        components: {
            DC,
            DCLogo,
            Hamburger,
            Chinese,
            SocialMediaButton
        },
        setup() {

            const { modeClass } = interactWithMenu()
            const { setReference: setLinksReference } = useLoopReferences<HTMLUListElement>()
            const { show, toggle, isFluid } = useMobileMenu()

            const {
                menuStyle,
                menuRef,
                menuClasses,
                clearMenuBackground,
                hideMenu,
                showMenu,
                isMenuHidden
            } = useStickyMenu()

            const { menu } = useTranslation()
            const { isCNLocale, switchLocale } = useCms()

            return {
                Route,
                Locale,
                modeClass,
                toggle,
                show,
                menuStyle,
                menuRef,
                menuClasses,
                data: menu,
                isFluid,
                isCNLocale,
                switchLocale,
                setLinksReference,
                clearMenuBackground,
                hideMenu,
                showMenu,
                isMenuHidden,
                socialMedia: [
                    {
                        name: 'LinkedIn',
                        color: '#4285EB',
                        link: 'https://linkedin.com/company/digital-creative-asia/'
                    },
                    {
                        name: 'Instagram',
                        color: '#F2994A',
                        link: 'https://instagram.com/dc.asia'
                    }
                ]
            }

        }
    })

</script>

<style lang="scss">

    @include component(menu) {

        z-index: 99;
        transition: margin-top 250ms linear, opacity 300ms linear, visibility 3000ms linear, background-color 200ms linear;
        will-change: margin-top, visibility, opacity, background-color;

        ul, ul li, svg {

            transition: opacity 250ms ease, color 250ms ease;

        }

        @include option(show-menu) {

            @include part(dc) {

                path {

                    fill: white;

                }

            }

            @include part(hamburger) {

                border: solid white 1px;

                span {

                    background: white;

                }

            }

            @include part(locale) {

                color: white;

                path {

                    fill: white;

                }

            }

            @include part(items) {

                @include part(link) {

                    @include option(contact) {

                        color: white !important;

                    }

                }

            }

        }

        @include option(hidden) {

            opacity: 0 !important;

        }

        @include option(scrolling-up) {

            @include part(new-logo) {

                transform: scale(0.8125);
                transform-origin: 0 50%;

            }

        }

        @include option(scrolling-down) {

            border-bottom: none !important;

            @screen md {

                visibility: hidden;
                opacity: 0;

                @include part(dc) {

                    visibility: hidden;
                    opacity: 0;

                }

                @include part(items) {

                    visibility: hidden;
                    opacity: 0;

                }

            }

        }

        @include part(new-logo) {

            @apply hidden;

            @screen lg {

                @apply flex items-start justify-start;

            }

        }

        @include part(dc) {

            @apply pl-0 flex items-start justify-start;

            @screen lg {

                padding-left: 42px;

            }

            @include part(logo) {

                width: 39px;
                height: 19px;

                @screen md {

                    width: 44px;
                    height: 24px;

                }

                &:hover {

                    opacity: 0.6;

                }

            }

        }

        @include part(items) {

            @apply font-medium;

            font-size: 16px;

            @include option(show) {

                @apply bg-black text-white text-2xl-mo font-bold w-screen h-full flex flex-col justify-center fixed left-0 top-0;

                @include part(link) {

                    a {

                        @apply text-white;

                    }

                }

            }

            @include part(link) {

                a {

                    @apply flex relative font-mabry-pro items-center justify-center;

                    transition: background-color 250ms ease;

                    @screen lg {

                        @apply px-7;

                        height: 56px;

                    }

                }

                @include option(contact) {

                    @screen lg {

                        @apply ml-7;

                        border: solid 1px;

                    }

                    a {

                        letter-spacing: 1px;
                        transition: background-color 250ms ease, color 250ms ease, border 250ms ease;

                        @screen lg {

                            @include option(zh) {

                                font-size: 12px;

                            }

                            font-size: 10px;

                            @apply w-36 uppercase px-0 font-body;

                        }

                    }

                }

            }

        }

        @include part(locale) {

            width: 50px;
            height: 58px !important;

            @apply hidden;

            @screen lg {

                width: 100px;

            }

            @include part(inner) {

                @apply cursor-pointer;

                transition: background-color 250ms ease, color 150ms ease;

            }

            @include option(small) {

                @apply flex justify-center items-center;

            }

            @screen lg {

                @apply h-full flex justify-center items-center;

            }

        }

        @include part(close) {

            @apply flex items-center justify-center bg-white rounded;

            width: 36px;
            height: 32px;
            border-radius: 100px;

        }

        @include part(hamburger) {

            @apply flex items-center justify-center h-8 w-9;

        }

        @include option(light) {

            top: 0;

        }

        @include option(light, light-no-transition) {

            right: 0;
            left: 0;

            ul li, svg {

                @screen lg {

                    opacity: 0.7;

                }

            }

            ul li:hover {

                opacity: 1;

            }

        }

        @keyframes enter-logo {

            0% {

                top: -80px;

            }

            100% {

                top: 0%;

            }

        }

    }

</style>
